import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

type CarouselReturnType<El> = {
  /** 次の要素にスクロール */
  next: () => void
  /** 前の要素にスクロール */
  prev: () => void
  /** 指定したindexにスクロール */
  go: (index: number) => void
  /** 最初の要素にスクロール */
  goFirst: () => void
  /** 最後の要素にスクロール */
  goLast: () => void
  /** 現在表示されているindex */
  currentIndex: number
  /** コンテナのref */
  ref: RefObject<El | null>
}

/**
 * カルーセルのコンテナと操作関数を返す
 * @example
 * ```tsx
 * const Carousel = ({ images }: { images: string[] }) => {
 *   const { next, prev, ref } = useCarousel();
 *   return (
 *     <div>
 *       <button onClick={prev}>←</button>
 *       <ul ref={ref} className="...">
 *         <li key={item}><img src={item} alt="" /></li>
 *       </ul>
 *       <button onClick={next}>→</button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useCarousel = <
  El extends HTMLElement = HTMLElement,
>(): CarouselReturnType<El> => {
  // 内部的なindex。連続で「次へ」を押した際に押した数だけ進むようにする
  const internalCurrentIndex = useRef(0)
  // 返り値として使うindex。スクロール位置に応じて変化する
  const [currentIndex, setCurrentIndex] = useState(0)

  const ref = useRef<El>(null)

  useEffect(() => {
    if (!ref.current) return
    const container = ref.current
    let timeoutId: number
    const onScroll = () => {
      window.clearTimeout(timeoutId)
      timeoutId = window.setTimeout(() => {
        // スクロールが止まった際にindexを更新する
        setCurrentIndex((prev) => {
          const index = Math.round(container.scrollLeft / container.clientWidth)
          // ボタンなどとは別で、ユーザーの手でスクロールした際に内部indexを更新する
          // ただし、連続で「次へ」を押した際に押した数だけ進むようにprevと比較する
          if (prev !== index) internalCurrentIndex.current = index
          return index
        })
      }, 100)
    }
    container.addEventListener('scroll', onScroll)
    return () => {
      container.removeEventListener('scroll', onScroll)
    }
  }, [])

  // next/prev/go関数
  // いずれもスクロールのみ行い、indexの更新はonScrollで行う
  const next = useCallback(() => {
    if (!ref.current) return
    const children = ref.current.children
    const nextIndex = (internalCurrentIndex.current + 1) % children.length
    children[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
    internalCurrentIndex.current = nextIndex
  }, [])

  const prev = useCallback(() => {
    if (!ref.current) return
    const children = ref.current.children
    const prevIndex =
      (internalCurrentIndex.current - 1 + children.length) % children.length
    children[prevIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
    internalCurrentIndex.current = prevIndex
  }, [])

  const go = useCallback((index: number) => {
    if (!ref.current) return
    const children = ref.current.children
    children[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
    internalCurrentIndex.current = index
  }, [])

  const goFirst = useCallback(() => go(0), [go])
  const goLast = useCallback(() => {
    if (!ref.current) return
    const children = ref.current.children
    go(children.length - 1)
  }, [go])

  return {
    next,
    prev,
    go,
    goFirst,
    goLast,
    currentIndex,
    ref,
  }
}
